const LOCALE_STORAGE_KEY = 'ty_locale'
const LOCALE_EVENT = 'ty:localechange'
const SUPPORTED_LOCALES = new Set(['zh', 'en', 'zh_CN', 'en_US'])

function detectInitialLocale() {
  try {
    const saved = window.localStorage.getItem(LOCALE_STORAGE_KEY)
    if (SUPPORTED_LOCALES.has(saved)) return saved.startsWith('en') ? 'en' : 'zh'
  } catch {
    // ignore localStorage failures
  }
  return 'zh'
}

let currentLocale = detectInitialLocale()
let fetchPatched = false

function buttonText(locale) {
  return locale === 'en' ? 'EN' : '中文'
}

function buttonTitle(locale) {
  return locale === 'en' ? 'Switch to Chinese' : '切换到英文'
}

function localeOptionLabel(locale) {
  return locale === 'en' ? '🇺🇸 English' : '🇨🇳 中文'
}

export function localeHeaderValue(locale = currentLocale) {
  return locale === 'en' ? 'en-US,en;q=0.9' : 'zh-CN,zh;q=0.9'
}

function applyDocumentLocale() {
  document.documentElement.lang = currentLocale === 'en' ? 'en' : 'zh-CN'
}

function saveLocale(locale) {
  try {
    window.localStorage.setItem(LOCALE_STORAGE_KEY, locale === 'en' ? 'en_US' : 'zh_CN')
  } catch {
    // ignore localStorage failures
  }
}

function updateSwitcher() {
  const select = document.getElementById('global-locale-switcher')
  if (!select) return
  select.value = currentLocale
  select.title = buttonTitle(currentLocale)
  select.setAttribute('aria-label', buttonTitle(currentLocale))
}

export function getLocale() {
  return currentLocale
}

export function setLocale(nextLocale) {
  const locale = nextLocale === 'en' ? 'en' : 'zh'
  if (locale === currentLocale) return
  currentLocale = locale
  saveLocale(locale)
  applyDocumentLocale()
  updateSwitcher()
  window.dispatchEvent(new CustomEvent(LOCALE_EVENT, { detail: { locale } }))
}

export function toggleLocale() {
  setLocale(currentLocale === 'en' ? 'zh' : 'en')
}

export function onLocaleChange(handler) {
  const listener = (event) => {
    handler(event.detail?.locale || getLocale())
  }
  window.addEventListener(LOCALE_EVENT, listener)
  return () => window.removeEventListener(LOCALE_EVENT, listener)
}

function ensureStyles() {
  if (document.getElementById('global-locale-switcher-style')) return
  const style = document.createElement('style')
  style.id = 'global-locale-switcher-style'
  style.textContent = `
    .global-locale-slot {
      display: inline-flex;
      align-items: center;
      justify-content: flex-end;
      flex: 0 0 auto;
    }

    .global-locale-slot--nav {
      list-style: none;
      margin: 0;
      padding: 0;
    }

    .global-locale-slot--auth {
      width: 100%;
      margin-bottom: 18px;
    }

    .global-locale-slot--workspace {
      margin-left: 8px;
    }

    #global-locale-switcher {
      min-width: 108px;
      height: 40px;
      padding: 0 24px 0 0;
      border: 0;
      border-radius: 0;
      background: transparent;
      color: #0f172a;
      font: 600 15px/1.1 "Inter", system-ui, -apple-system, "PingFang SC", sans-serif;
      letter-spacing: 0.02em;
      cursor: pointer;
      box-shadow: none;
      appearance: none;
      -webkit-appearance: none;
      -moz-appearance: none;
      background-image:
        linear-gradient(45deg, transparent 50%, #64748b 50%),
        linear-gradient(135deg, #64748b 50%, transparent 50%);
      background-position:
        calc(100% - 10px) calc(50% - 2px),
        calc(100% - 4px) calc(50% - 2px);
      background-size: 6px 6px, 6px 6px;
      background-repeat: no-repeat;
    }

    #global-locale-switcher:hover {
      color: #2563eb;
    }

    @media (max-width: 640px) {
      #global-locale-switcher {
        min-width: 98px;
        height: 38px;
        padding: 0 22px 0 0;
        font-size: 14px;
      }

      .global-locale-slot--auth {
        margin-bottom: 14px;
      }
    }
  `
  document.head.appendChild(style)
}

function getMountTarget() {
  const navLinks = document.querySelector('.nav-links')
  if (navLinks) return { container: navLinks, mode: 'nav' }

  const accountActions = document.querySelector('.account-actions')
  if (accountActions) return { container: accountActions, mode: 'inline' }

  const dashboardActions = document.querySelector('.dashboard-actions')
  if (dashboardActions) return { container: dashboardActions, mode: 'inline' }

  const adminActions = document.querySelector('.admin-actions')
  if (adminActions) return { container: adminActions, mode: 'inline' }

  const menubar = document.getElementById('menubar')
  if (menubar) return { container: menubar, mode: 'workspace' }

  return null
}

function ensureSwitcher() {
  let slot = document.getElementById('global-locale-slot')
  if (!slot) {
    const target = getMountTarget()
    if (!target?.container) return
    slot = document.createElement(target.mode === 'nav' ? 'li' : 'div')
    slot.id = 'global-locale-slot'
    slot.className = `global-locale-slot global-locale-slot--${target.mode}`

    if (target.mode === 'workspace') {
      const statusPill = document.getElementById('status-pill')
      if (statusPill?.parentNode === target.container) {
        target.container.insertBefore(slot, statusPill)
      } else {
        target.container.appendChild(slot)
      }
    } else {
      target.container.appendChild(slot)
    }
  }

  let select = document.getElementById('global-locale-switcher')
  if (!select) {
    select = document.createElement('select')
    select.id = 'global-locale-switcher'
    select.innerHTML = `
      <option value="zh">${localeOptionLabel('zh')}</option>
      <option value="en">${localeOptionLabel('en')}</option>
    `
    select.addEventListener('change', () => {
      setLocale(select.value)
    })
    slot.appendChild(select)
  } else if (select.parentNode !== slot) {
    slot.appendChild(select)
  }

  updateSwitcher()
}

function patchFetch() {
  if (fetchPatched || typeof window.fetch !== 'function') return
  fetchPatched = true
  const nativeFetch = window.fetch.bind(window)

  window.fetch = (input, init = undefined) => {
    const request = input instanceof Request ? input : null
    const baseInit = init ? { ...init } : {}
    const headers = new Headers(request ? request.headers : baseInit.headers || {})
    if (!headers.has('Accept-Language')) {
      headers.set('Accept-Language', localeHeaderValue())
    }
    if (request) {
      return nativeFetch(new Request(request, { ...baseInit, headers }))
    }
    return nativeFetch(input, { ...baseInit, headers })
  }
}

export function initLocaleUi() {
  applyDocumentLocale()
  ensureStyles()
  ensureSwitcher()
  patchFetch()
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initLocaleUi, { once: true })
} else {
  initLocaleUi()
}
